/*
 * KeywordForm — 태그·키워드 칩을 입력·추가·삭제하는 공통 컴포넌트 (D-09 §15.50).
 * Figma: PT8wG7aRcWg4CerPViHOI4 / 4370:2678 (readonly · 첫 로딩 시 · 태그 추가된 상태).
 *
 * 기본 기능: 입력 후 Enter → 추가, 빈 입력에서 Backspace 또는 × → 삭제, 중복·개수 초과 → 경고 문구.
 * 나머지(드래그 순서변경·모두 삭제·칩 클릭·정해진 목록)는 해당 콜백/prop을 넘길 때만 켜진다.
 * 태그 값과 저장(API)은 소비처가 들고, 이 컴포넌트는 입력·표시만 맡는다.
 */
import React, { useRef, useState } from 'react';
import { cx } from '../cx';
import { Tag } from './Tag';
import { Checkbox } from './Checkbox';

export interface KeywordFormProps {
  tags: string[];
  /** edit = Input과 같은 박스(등록·수정) · readonly = 칩만(× 는 onRemove 있을 때 유지) */
  mode?: 'edit' | 'readonly';
  onAdd?: (value: string) => void;
  onRemove?: (index: number) => void;
  placeholder?: string;
  /** 아래 안내 메시지. 경고가 생기면 경고 문구로 대체된다 */
  help?: string;
  maxLength?: number;
  maxCount?: number;
  disabled?: boolean;
  /** 제공 시 칩 드래그로 순서 변경 */
  onReorder?: (from: number, to: number) => void;
  /** 제공 시 '모두 삭제' 버튼 */
  onClear?: () => void;
  /** 칩 본문 클릭(× 클릭과 분리) — 예: 본문 삽입, 선택 사진에 적용 */
  onTagClick?: (tag: string, index: number) => void;
  /** 정해진 목록(DB). 박스를 누르면 목록이 열리고 입력하면 걸러진다 */
  options?: string[];
  /** false면 options 에 있는 값만 추가 가능 (기본 true) */
  allowCustom?: boolean;
  /** 추가 전 값 정규화 — 예: '#' 처리 */
  normalize?: (value: string) => string;
  /** true 인 칩을 강조 표시 — 예: 이번에 새로 추가된 태그 */
  highlight?: (tag: string, index: number) => boolean;
  className?: string;
  'aria-label'?: string;
}

export function KeywordForm({
  tags,
  mode = 'edit',
  onAdd,
  onRemove,
  placeholder = '태그입력 후 엔터',
  help,
  maxLength,
  maxCount,
  disabled,
  onReorder,
  onClear,
  onTagClick,
  options,
  allowCustom = true,
  normalize,
  highlight,
  className,
  'aria-label': ariaLabel,
}: KeywordFormProps) {
  const [draft, setDraft] = useState('');
  const [warn, setWarn] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragFrom = useRef<number | null>(null);

  const editable = mode === 'edit' && !disabled && !!onAdd;
  const removable = !disabled && !!onRemove;
  const filtered = options?.filter((o) => o.includes(draft.trim())) ?? [];

  const submit = (raw: string) => {
    const value = normalize ? normalize(raw.trim()) : raw.trim();
    if (!value || !onAdd) return;
    if (tags.includes(value)) return setWarn('이미 추가된 태그입니다.');
    if (maxCount != null && tags.length >= maxCount) return setWarn(`최대 ${maxCount}개까지 등록할 수 있습니다.`);
    if (options && !allowCustom && !options.includes(value)) return setWarn('목록에 있는 항목만 추가할 수 있습니다.');
    onAdd(value);
    setDraft('');
    setWarn(null);
    setActive(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // 한글 조합 중 Enter 는 조합 확정용
    if (e.key === 'Enter') {
      e.preventDefault();
      submit(open && active >= 0 ? filtered[active] : draft);
    } else if (e.key === 'Backspace' && !draft && removable && tags.length) {
      onRemove!(tags.length - 1);
      setWarn(null);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setDraft('');
      setWarn(null);
    } else if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      const n = filtered.length;
      if (n) setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
    }
  };

  const chips = tags.map((tag, i) => (
    <Tag
      key={`${tag}-${i}`}
      className={cx(onTagClick && 'kf-tag-click', highlight?.(tag, i) && 'kf-tag-hl')}
      onRemove={removable ? () => { onRemove!(i); setWarn(null); } : undefined}
      onClick={onTagClick ? (e) => {
        // Tag 의 × 버튼 클릭이 span 까지 버블링되므로 칩 클릭과 분리
        if ((e.target as HTMLElement).closest('button')) return;
        onTagClick(tag, i);
      } : undefined}
      // ponytail: HTML5 드래그는 마우스 전용 — 터치 순서변경이 필요해지면 dnd-kit 도입
      draggable={!!onReorder && !disabled}
      onDragStart={onReorder ? () => { dragFrom.current = i; } : undefined}
      onDragOver={onReorder ? (e) => e.preventDefault() : undefined}
      onDrop={onReorder ? () => {
        const from = dragFrom.current;
        dragFrom.current = null;
        if (from != null && from !== i) onReorder(from, i);
      } : undefined}
    >
      {tag}
    </Tag>
  ));

  const message = warn ?? help;

  return (
    <div
      className={cx('kf', className)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      {mode === 'readonly' ? (
        tags.length > 0 && <div className="kf-chips" aria-label={ariaLabel}>{chips}</div>
      ) : (
        // kf-field = 목록 드롭다운(.menu top:100%)의 기준 상자
        <div className="kf-field">
          <div
            className={cx('input', 'kf-box', disabled && 'is-disabled', open && 'is-open')}
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('button, .tag')) return;
              inputRef.current?.focus();
              if (options) setOpen(true);
            }}
          >
            {chips}
            {editable && (
              <input
                ref={inputRef}
                className="kf-input"
                value={draft}
                maxLength={maxLength}
                placeholder={placeholder}
                aria-label={ariaLabel ?? placeholder}
                onChange={(e) => { setDraft(e.target.value); setWarn(null); setActive(-1); if (options) setOpen(true); }}
                onKeyDown={onKeyDown}
                onFocus={() => options && setOpen(true)}
              />
            )}
          </div>
          {open && editable && options && (
            <div className="menu menu-multi show kf-menu" role="listbox" aria-multiselectable="true">
              {filtered.length === 0 && <div className="combo-empty">{allowCustom ? 'Enter 로 추가' : '일치하는 항목이 없습니다'}</div>}
              {filtered.map((opt, i) => {
                const idx = tags.indexOf(opt);
                return (
                  <div
                    key={opt}
                    role="option"
                    aria-selected={idx >= 0}
                    className={cx('menu-item', idx >= 0 && 'selected', i === active && 'active')}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => (idx >= 0 ? removable && onRemove!(idx) : submit(opt))}
                  >
                    <Checkbox bare checked={idx >= 0} readOnly tabIndex={-1} />
                    {opt}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {(message || (onClear && tags.length > 0 && !disabled)) && (
        <div className="kf-foot">
          <p className={cx('kf-help', warn && 'is-warn')} role={warn ? 'alert' : undefined}>{message}</p>
          {onClear && tags.length > 0 && !disabled && (
            <button type="button" className="kf-clear" onClick={() => { onClear(); setWarn(null); }}>
              모두 삭제
            </button>
          )}
        </div>
      )}
    </div>
  );
}
